import { CategoryFilter } from '@yi/core';

import { CardType } from '~/types';

import { getAnalytics } from './getAnalytics';

interface TopGoals {
  [k: string]: string | undefined;
}

export type TrackOpts = SegmentAnalytics.SegmentOpts;
const defaultOpts: TrackOpts = { integrations: { Optimizely: false } };

export const sendOnboardingGoals = (goals: Array<string>) => {
  const { track, segmentData } = getAnalytics();
  const topGoals: TopGoals = { first_goal: '', second_goal: '', third_goal: '' };
  if (goals.length) {
    if (goals[0]) topGoals.first_goal = goals[0];
    if (goals[1]) topGoals.second_goal = goals[1];
    if (goals[2]) topGoals.third_goal = goals[2];
  }
  track('Top Onboard Goals', { ...topGoals, ...segmentData }, defaultOpts);
};

export const sendSlideCompleted = (slide: string) => {
  const { track, segmentData } = getAnalytics();
  track('Completed Onboard Slide', { slide, ...segmentData }, defaultOpts);
};

export const sendHereForTeacher = (answer: 'yes' | 'no') => {
  const { track, segmentData } = getAnalytics();
  track('Onboard Teacher Question', { answer, ...segmentData }, defaultOpts);
};

export const sendLeftNavClicked = () => {
  const { track, segmentData } = getAnalytics();
  track('Left Nav Opened', { ...segmentData }, defaultOpts);
};

export const sendLeftNavLinked = (path: string) => {
  const { track, segmentData } = getAnalytics();
  track('Left Nav Linked', { path, ...segmentData }, defaultOpts);
};

export const sendUserMenuLinked = (path: string) => {
  const { track, segmentData } = getAnalytics();
  track('User Menu Linked', { path, ...segmentData }, defaultOpts);
};

export const sendGoalChipClicked = (goal: string) => {
  const { track, segmentData } = getAnalytics();
  track('Goal Chip Clicked', { goal, ...segmentData }, defaultOpts);
};

export const sendQuickstartHomeClicked = (button: string) => {
  const { track, segmentData } = getAnalytics();
  track('Quickstart Home Clicked', { button, ...segmentData }, defaultOpts);
};

export const sendDashboardCardClicked = (carousel: string) => {
  const { track, segmentData } = getAnalytics();
  track('Dashboard Card Clicked', { carousel, ...segmentData }, defaultOpts);
};

export const sendCheckStatsClicked = () => {
  const { track, segmentData } = getAnalytics();
  track('Check Stats Clicked', { ...segmentData }, defaultOpts);
};

export const sendPlaylistShared = (playlistUrl: string) => {
  const { track, segmentData } = getAnalytics();
  track('Playlist Shared', { playlistUrl, ...segmentData }, defaultOpts);
};

type Program = { title: string; teacher: string };
export const sendProgramStarted = (program: Program) => {
  const { track, segmentData } = getAnalytics();
  track('Program Started', { ...program, ...segmentData }, defaultOpts);
};

export const sendModuleCompleted = (program: Program & { module: string }) => {
  const { track, segmentData } = getAnalytics();
  track('Module Completed', { ...program, ...segmentData }, defaultOpts);
};

export const sendPracticeAssistantOpened = () => {
  const { track, segmentData } = getAnalytics();
  track('Practice Assistant Opened', { ...segmentData });
};

export const sendPracticeAssistantProvidedResults = () => {
  const { track, segmentData } = getAnalytics();
  track('Practice Assistant Provided Results', { ...segmentData });
};

export const sendjwPlayerLoaded = (loadTime: string, contentId: string) => {
  const { track, segmentData, simpleUserTraitsWithIntegrations } = getAnalytics();
  track(
    'JWPlayer First Frame Loaded',
    { time_to_start: loadTime, id: contentId, ...segmentData },
    simpleUserTraitsWithIntegrations,
  );
};

export const sendFiltersButtonClicked = (pathName: string) => {
  const { track, segmentData } = getAnalytics();
  track('Filters Button Clicked', { pathName, ...segmentData }, defaultOpts);
};

export const sendFilterCategoriesExpanded = (expandedCategories: CategoryFilter[]) => {
  const { track, segmentData } = getAnalytics();
  track(
    'Filter Categories Expanded',
    {
      expandedCategories,
      ...segmentData,
    },
    defaultOpts,
  );
};

export const sendSeeResultsClicked = () => {
  const { track, segmentData } = getAnalytics();
  track('See Results Clicked', { ...segmentData }, defaultOpts);
};

export const sendClosedCaptionSelected = () => {
  const { track, segmentData } = getAnalytics();
  track('Closed Caption Selected', { ...segmentData }, defaultOpts);
};

export const sendDurationChipClicked = (duration: string) => {
  const { track, segmentData, simpleUserTraitsWithIntegrations } = getAnalytics();
  track('Duration Chip Clicked', { duration, ...segmentData }, simpleUserTraitsWithIntegrations);
};

export const sendDurationCarouselCardClicked = (args: {
  class_id: string;
  title: string;
  author: string;
}) => {
  const { track, segmentData, simpleUserTraitsWithIntegrations } = getAnalytics();
  track(
    'Duration Carousel Card Clicked',
    { ...args, ...segmentData },
    simpleUserTraitsWithIntegrations,
  );
};

export const sendDurationCarouselClassViewed = (
  completionPercent: string,
  title: string,
  author: string,
) => {
  const { track, segmentData } = getAnalytics();
  track('Duration Carousel Class Viewed', {
    completionPercent,
    title,
    author,
    ...segmentData,
  });
};

export const sendContentCompleted = () => {
  const { track, segmentData } = getAnalytics();
  track('Content Completed', { ...segmentData }, defaultOpts);
};

export const sendCardPlayButtonClicked = (args: { title: string; cardType: CardType }) => {
  const { track, segmentData } = getAnalytics();
  track('Card Play Button Clicked', { ...args, ...segmentData });
};

export const sendCardKebabMenuClicked = (args: { title: string; cardType: CardType }) => {
  const { track, segmentData } = getAnalytics();
  track('Card Kebab Menu Clicked', { ...args, ...segmentData });
};

export const sendAddToPlaylistClicked = (args: { title: string; cardType: CardType }) => {
  const { track, segmentData } = getAnalytics();
  track('Card Add to Playlist Clicked', { ...args, ...segmentData });
};

export const sendAddToMyLibraryClicked = (args: { title: string; cardType: CardType }) => {
  const { track, segmentData } = getAnalytics();
  track('Card Add to My Library Clicked', { ...args, ...segmentData });
};

export const sendRemoveFromMyLibraryClicked = (args: { title: string; cardType: CardType }) => {
  const { track, segmentData } = getAnalytics();
  track('Card Remove from My Library Clicked', { ...args, ...segmentData });
};

export const sendMoreInfoClicked = (args: { title: string; cardType: CardType }) => {
  const { track, segmentData } = getAnalytics();
  track('Card More Info Clicked', { ...args, ...segmentData });
};
